import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, mergeMap } from 'rxjs/operators';
import { retryTransientHttpErrors } from '../../../core/operators/retry-transient-http-error.operator';
import { NetworkCallService } from '../../../core/services/network-call.service';
import * as marketingOptionsActions from '../actions/marketing-options.actions';
import {
  IMarketingRequest,
  IMarketingResponse,
  IRegulatoryRequest,
  ITermsAndConditions
} from '../models/marketing-options.model';
import * as actions from '../actions';
import { FileType, ResponseContentType, RouteURL } from '../../../../../src/app/shared/enums';
import { IHttpRequestOptions } from '../../../../../src/app/shared/interfaces/request-options';
import { downloadFile } from '../../../../../src/app/shared/util.helper';
import { HelpersService } from '../../../core/services/helpers.service';

@Injectable()
export class MarketingOptionsEffects {
  constructor(
    private actions$: Actions,
    private networkCallService: NetworkCallService,
    private router: Router,
    private helperService: HelpersService
  ) {}

  marketingOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(marketingOptionsActions.loadMarketingOptions),
      switchMap(action =>
        this.networkCallService.put<IMarketingRequest, string>('Customer/permissions', action.request).pipe(
          map(response => {
            this.helperService.notifySuccess(`Options accepted`, 200, 3000);

            return marketingOptionsActions.loadMarketingOptionsSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(marketingOptionsActions.loadMarketingOptionsFailure(error));
          })
        )
      )
    )
  );

  marketing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadGetMarketingOptions),
      switchMap(action =>
        this.networkCallService.get<IMarketingResponse>('Customer/permissions').pipe(
          map(response => {
            return actions.loadGetMarketingOptionsSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadGetMarketingOptionsFailure(error));
          })
        )
      )
    )
  );

  getRegulatory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadGetRegulatory),
      switchMap(action =>
        this.networkCallService.get<ITermsAndConditions>('Customer/permissionsTermsAndConditions').pipe(
          map(response => {
            return actions.loadGetRegulatorySuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadGetRegulatoryFailure(error));
          })
        )
      )
    )
  );

  acceptRegulatory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadAcceptRegulatory),
      switchMap(action =>
        this.networkCallService.put<IRegulatoryRequest, string>('Customer/regulatoryPermissions', action.request).pipe(
          mergeMap(response => {
            this.helperService.notifySuccess(`Options accepted`, 200, 3000);
            return [
              actions.loadAcceptRegulatorySuccess({
                response
              }),
              actions.loadRegulatorySettingsFirstTimeCustomers()
            ];
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadAcceptRegulatoryFailure(error));
          })
        )
      )
    )
  );

  regulatorySettingsFirstTimeCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadRegulatorySettingsFirstTimeCustomers),
      switchMap(action =>
        this.networkCallService.put<null, string>('Customer/privacySettingsSetForFirstTimeCustomer').pipe(
          map(response => {
            this.router.navigateByUrl(RouteURL.ADMIN_DASHBOARD);
            return actions.loadRegulatorySettingsFirstTimeCustomersSuccess({
              response
            });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadRegulatorySettingsFirstTimeCustomersFailure(error));
          })
        )
      )
    )
  );

  downloadDocs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.loadDownloadPopiDocs),
      switchMap(action => {
        const options: IHttpRequestOptions = {
          responseType: ResponseContentType.BLOB
        };
        return this.networkCallService.get<string>(`File/${action.documentName}/download`, options).pipe(
          map(response => {
            downloadFile(response, FileType.ALL_IMAGES, `${action.documentName}`);
            this.helperService.notifySuccess(`Document downloaded`, 200, 3000);

            return actions.loadDownloadPopiDocsSuccess({ reponse: response });
          }),
          retryTransientHttpErrors(),
          catchError(error => {
            return of(actions.loadDownloadPopiDocsFailure(error));
          })
        );
      })
    );
  });
}
